import { useId } from "react"
import { cn } from "@/lib/utils"

// Filled, logo-style icons for the tab bar and reader settings button. All
// paint with `currentColor` and are decorative unless given an aria-label.
type IconProps = React.ComponentProps<"svg">

const iconDefaults = {
  fill: "currentColor",
  "aria-hidden": true,
} as const

/** The open book from the logo (`public/logo.svg`, `path30-3`). */
export const BookIcon = ({ className, ...props }: IconProps) => (
  <svg
    viewBox="0 0 100 100"
    {...iconDefaults}
    className={cn("size-6", className)}
    {...props}
  >
    <path
      transform="matrix(1.3220202,0,0,1.3220202,-9.6289825,-153.58423)"
      d="m 20.343347,136.47907 -2.205408,34.77422 c 7.996608,-3.17538 17.0703,-4.36103 27.038442,0 9.434197,-4.138 18.531784,-3.35699 27.479905,0 l -0.227517,-12.89477 c -2.166804,-5.88282 -4.504008,-10.35236 -7.022823,-13.40863 0.221509,-0.84011 4.882751,-1.25977 6.740122,5.6e-4 l -0.257528,-8.61958 c -9.23329,-2.82393 -17.862865,-3.36486 -25.757307,0.25584 -7.552636,-3.3606 -16.097125,-3.37731 -25.786199,-0.10747 z"
    />
  </svg>
)

const GEAR_TEETH = [0, 60, 120, 180, 240, 300]

export const GearIcon = ({ className, ...props }: IconProps) => {
  // The hub hole is a mask, so its id must be unique per instance.
  const maskId = useId()
  return (
    <svg
      viewBox="0 0 24 24"
      {...iconDefaults}
      className={cn("size-6", className)}
      {...props}
    >
      <mask id={maskId}>
        <rect width="24" height="24" fill="#fff" />
        <path
          d="M12 9.1c1.7-.1 3 1.2 2.9 2.9.1 1.7-1.3 3-2.9 2.9-1.7.1-3-1.2-2.9-2.9-.1-1.7 1.2-3 2.9-2.9z"
          fill="#000"
        />
      </mask>
      <g mask={`url(#${maskId})`}>
        <path d="M12 4.6c4.2-.3 7.6 3.1 7.4 7.4.2 4.2-3.2 7.6-7.4 7.4-4.2.2-7.6-3.2-7.4-7.4-.2-4.3 3.2-7.7 7.4-7.4z" />
        {GEAR_TEETH.map((angle) => (
          <rect
            key={angle}
            x="9.9"
            y="1.6"
            width="4.2"
            height="5"
            rx="1.5"
            transform={angle ? `rotate(${angle} 12 12)` : undefined}
          />
        ))}
      </g>
    </svg>
  )
}

export const PersonIcon = ({ className, ...props }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    {...iconDefaults}
    className={cn("size-6", className)}
    {...props}
  >
    <path d="M12.1 3.2c2.4-.1 4.2 1.9 4.1 4.3.1 2.4-1.8 4.3-4.2 4.2-2.4.1-4.2-1.8-4.1-4.2-.1-2.5 1.8-4.4 4.2-4.3z" />
    <path d="M3.6 21.2c.4-4.6 3.9-7.6 8.4-7.6 4.5 0 8 3 8.4 7.6-2.8-1.2-5.6-1.3-8.4-.1-2.8-1.2-5.6-1.1-8.4.1z" />
  </svg>
)

/** Book + "yomimono" in the brand pink. Size it with a text size class. */
export const Wordmark = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    className={cn(
      "inline-flex items-center gap-[0.3em] font-wordmark leading-none font-bold text-brand",
      className
    )}
    {...props}
  >
    <BookIcon className="size-[1.2em]" />
    yomimono
  </span>
)
